/**
 * ActionDeclinePage - Handle structural action decline email links
 * 
 * Flow:
 * 1. Extract token from URL
 * 2. Process decline
 * 3. Show result and remove token from URL
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { EmailActionResultPage } from "./EmailActionResultPage";
import api from "../lib/api";

export default function ActionDeclinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading");
  const [title, setTitle] = useState(null);
  const [message, setMessage] = useState(null);
  const [contextInfo, setContextInfo] = useState(null);
  const [processed, setProcessed] = useState(false);

  // Remove token from URL after processing
  const removeTokenFromUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Process token
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setTitle("Invalid link");
      setMessage("This link is missing required information.");
      return;
    }

    if (processed) return;

    const processToken = async () => {
      try {
        // First validate the token for context
        const validateRes = await api.get(`/api/household/token/validate?token=${encodeURIComponent(token)}`);
        
        if (validateRes.ok) {
          const validateData = await validateRes.json();
          
          if (validateData.valid && validateData.context) {
            setContextInfo(validateData.context);
            
            // Check if already handled
            if (validateData.context.status !== "pending") {
              setStatus("already_handled");
              setTitle("Already processed");
              setMessage(`This change request has been ${validateData.context.status}.`);
              removeTokenFromUrl();
              setProcessed(true);
              return;
            }
          }
        }

        // Process the decline action
        const res = await api.post(`/api/household/token/action/decline?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (res.ok) {
          if (data.status === "already_handled") {
            setStatus("already_handled");
            setTitle("Already processed");
            setMessage(data.message);
          } else {
            setStatus("success");
            setTitle("Change declined");
            setMessage("The requested change has been declined. No changes have been made.");
          }
        } else if (res.status === 410) {
          setStatus("expired");
          setTitle("Link expired");
          setMessage(data.detail || "This link is no longer valid.");
        } else {
          setStatus("error");
          setMessage(data.detail || "Could not decline this change.");
        }

        removeTokenFromUrl();
        setProcessed(true);

      } catch (error) {
        console.error("Action decline error:", error);
        setStatus("error");
        setMessage("Something went wrong. Please try again from the app.");
        removeTokenFromUrl();
      }
    };

    processToken();
  }, [token, processed, removeTokenFromUrl]);

  return (
    <EmailActionResultPage
      status={status}
      title={title}
      message={message}
      primaryButtonLabel="Close"
      primaryButtonAction="/"
      contextInfo={contextInfo}
      showHomeButton={false}
    />
  );
}
